import { AlertPosition } from "@components/alert/Alert.types";
import { WithAlert, withAlert } from "@components/alert/withAlert";
import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import {
    fileNameToMime,
    getFileNameExtension,
    getUniqueFileName,
    GridMode,
    splitFileName
} from "@components/fileUploader/File.utils";
import {
    FileToolbarItem,
    getFileNameFromSrc,
    getFileViewer,
    IFileViewerProps,
    isFileOpenable
} from "@components/fileViewers/FileViewers.utils";
import { HotspotViewIds } from "@components/hotspots/HotspotViewIds";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { DocumentEntity, FileMetadataEntity, IFileMetadataEntity } from "@odata/GeneratedEntityTypes";
import { getCleanDraftKey, IFilesDefinition, viewGetExpanded } from "@pages/PageUtils";
import { getFocusableElements, isObjectEmpty } from "@utils/general";
import { removeWhiteSpace } from "@utils/string";
import React from "react";

import BusyIndicator from "../../components/busyIndicator";
import SmartField, {
    ISmartFieldChange,
    ISmartFieldTempDataActionArgs
} from "../../components/smart/smartField/SmartField";
import SmartFileUploader from "../../components/smart/smartFileUploader/SmartFileUploader";
import { FILES_API_URL } from "../../constants";
import { PaneStatus } from "../../enums";
import { ModelEvent } from "../../model/Model";
import BindingContext, { areBindingContextsDifferent, createPath, IEntity } from "../../odata/BindingContext";
import TestIds from "../../testIds";
import { getAlertFromError } from "../formView/Form.utils";
import { FormStorage } from "../formView/FormStorage";
import { FileViewStyled, FormAttachmentsFolderTitle } from "./FileView.styles";
import {
    ATTACHMENT_LOCAL_BC_PATH,
    getAllAttachmentsFromPaths,
    getFileNameWriteLineDef,
    getFiles
} from "./FileViewUtils";

interface IProps extends WithConfirmationDialog, WithAlert {
    paneStatus?: PaneStatus;
    formStorage: FormStorage;
    isVisible?: boolean;
    isBusy?: boolean;
    onNewFiles?: () => void;
    onFilesUploaded?: (isFirstUpload: boolean) => void;
    onFilesRemoved?: (isLastFile: boolean) => void;
    onDragEnter?: (event: DragEvent) => void;
    onDragLeave?: (event: DragEvent) => void;
    /** Can be used in case current entity uses FileView which has global file drop area.
     *otherFileDropArea is list of elements, that handles file drop for themselves, outside of the FileView.
     * When user hovers files over them, in case this FileDropArea is global,
     * we should disable hover state in this FileDropArea, so that use knows which of the drop areas will be used. */
    otherFileDropAreas?: HTMLElement[];

    ref?: React.RefObject<React.ReactElement>;
}

interface IState {
    gridMode?: GridMode;
}

class FileView extends React.PureComponent<IProps, IState> {
    formBcBackup: BindingContext = null;
    hasFormBcChanged = true;
    _fileViewRef: React.RefObject<HTMLDivElement> = React.createRef();

    state: IState = {
        gridMode: GridMode.EntityAttachments
    };

    componentDidUpdate(prevProps: IProps) {
        const { formStorage } = this.props;
        if (areBindingContextsDifferent(this.formBcBackup, formStorage?.data?.bindingContext)) {
            this.hasFormBcChanged = true;
        }

        // we want to automatically pre-open file, if only one is present
        // but only the first time FileView is opened
        // this should reset everytime selected row changes
        const isLoaded = !!(formStorage?.loaded && !formStorage?.isBusy() && !formStorage.loading);
        if (isLoaded && this.props.isVisible && this.hasFormBcChanged) {
            this.init();
        }

        if (viewGetExpanded(this.props.paneStatus, prevProps.paneStatus)) {
            this.focusView();
        }

        this.formBcBackup = formStorage?.data?.bindingContext;
    }

    get filesDef(): IFilesDefinition {
        return this.getFormDef()?.files || {};
    }

    get selectedFile(): IFileMetadataEntity {
        if (this.filesDef.singlePreview) {
            const bc = this.props.formStorage.data.bindingContext.navigate(this.filesDef.property);
            return this.props.formStorage.getValue(bc);
        }
        return this.props.formStorage?.data?.selectedFile;
    }

    getAttachmentsBc = (): BindingContext => {
        if (!this.filesDef.collection) {
            return null;
        }
        return this.props.formStorage.data.bindingContext.navigate(this.filesDef.collection);
    };

    getFormAttachments = (): IEntity[] => {
        if (!this.filesDef?.readOnlyAttachmentPaths?.length) {
            return [];
        }
        return getAllAttachmentsFromPaths(this.filesDef.readOnlyAttachmentPaths, this.props.formStorage);
    };

    getFileNameBc = (): BindingContext => {
        const storage = this.props.formStorage;
        const { collection, property, singlePreview } = this.filesDef;
        const namePath = createPath(this.filesDef.property, FileMetadataEntity.Name);
        if (singlePreview) {
            return storage.data.bindingContext.navigate(namePath);
        }
        const attachments = storage.getValueByPath(collection);
        const formsAttachments = this.getFormAttachments();
        const allAttachments = [...(attachments ?? []), ...(formsAttachments ?? [])] as IEntity[];
        const attachment = allAttachments?.find((attachment: IEntity) => attachment[property].Id === storage.data.selectedFile?.Id);

        if (!attachment) {
            return null;
        }

        if (attachment[ATTACHMENT_LOCAL_BC_PATH]) {
            return attachment[ATTACHMENT_LOCAL_BC_PATH].navigate(namePath);
        }

        return this.getAttachmentsBc()
            .addKey(attachment.Id)
            .navigate(namePath);
    };

    init = () => {
        this.formBcBackup = this.props.formStorage?.data?.bindingContext;
        this.hasFormBcChanged = false;
        this.initFiles();
        this.props.setAlert(null);
    };

    focusView() {
        // focus back arrow if there is
        if (this._fileViewRef.current) {
            const focusableElements = getFocusableElements(this._fileViewRef.current);
            const backArrowOrAttachment = Array.from(focusableElements)
                .find((el) => {
                    const testId = el.getAttribute("data-testid");
                    return (testId === TestIds.Button && el.getAttribute("data-hotspotid") === "Dashboard")
                        || testId === TestIds.Attachment;
                });
            backArrowOrAttachment?.focus();
        }
    }

    getFormDef = () => {
        return this.props.formStorage?.data?.definition;
    };

    isFormReadOnly = () => {
        return !this.props.formStorage?.data?.definition?.files?.enabledInReadOnlyMode && this.props.formStorage?.isReadOnly;
    };

    selectFile = (file: IFileMetadataEntity) => {
        this.props.formStorage.selectFile(file);
        this.forceUpdate();
    };

    handleFileChange = (file: IFileMetadataEntity) => {
        this.selectFile(file);
    };

    handleNewFiles = () => {
        this.props.onNewFiles?.();
        this.selectFile(null);
    };

    handleFilesUploaded = (files: IFileMetadataEntity[], isFirstUpload: boolean) => {
        const isSingle = files.length === 1 && getFiles(this.props.formStorage).length === 1;
        this.props.onFilesUploaded?.(isFirstUpload);
        this.props.formStorage.emitter.emit(ModelEvent.FilesUploaded, files);

        if (isSingle) {
            const file = files[0];
            if (isFileOpenable(file.Name)) {
                this.handleFileChange(file);
            }
        }
    };

    handleFilesRemoved = (files: IFileMetadataEntity[], isLastRemoved: boolean) => {
        this.props.onFilesRemoved?.(isLastRemoved);
    };

    handleReturnFromFile = () => {
        this.props.formStorage.cancelFields([this.getFileNameBc()]);
        this.selectFile(null);
        this.forceUpdate();
    };

    initFiles = () => {
        const filesDef = this.getFormDef()?.files;
        if (!filesDef) {
            return;
        }

        const hasFormAttachments = this.getFormAttachments()?.length > 0;
        const files = getFiles(this.props.formStorage);
        const getFileMeta = (file: IEntity) => this.filesDef.property ? file[this.filesDef.property] : file;

        let selectedFile: IFileMetadataEntity;

        if (hasFormAttachments) {
            // do nothing 
        } else if (files?.length === 1) {
            selectedFile = getFileMeta(files[0]);
        } else if (files?.length === 2) {
            const nonTxtFile = files.find((f: IEntity) => getFileNameExtension(getFileMeta(f)?.Name)?.toLowerCase() !== "txt");
            const hasTxtFile = files.some((f: IEntity) => getFileNameExtension(getFileMeta(f)?.Name)?.toLowerCase() === "txt");
            if (hasTxtFile && nonTxtFile) {
                selectedFile = getFileMeta(nonTxtFile);
            }
        }

        if (isFileOpenable(selectedFile?.Name)) {
            this.handleFileChange(selectedFile);
        } else if (isObjectEmpty(this.selectedFile)) {
            this.selectFile(null);
        }
    };

    handleFileRemove = async (src: string) => {
        const isConfirmed = await this.props.confirmationDialog.open({
            content: this.props.formStorage.t("Common:Confirmations.ConfirmDelete")
        });

        if (!isConfirmed) {
            return;
        }

        const formStorage = this.props.formStorage;
        const fileId = parseInt(getFileNameFromSrc(src));
        const attachmentsBc = this.getAttachmentsBc();
        const attachments: IEntity[] = formStorage.getValue(attachmentsBc);

        const attachmentId = attachments.find((attachment: IEntity) => attachment[this.filesDef.property].Id === fileId).Id;
        const isDraft = !!formStorage.data.entity[formStorage.data.definition?.draftDef?.draftProperty]?.Id;
        let path = attachmentsBc.toString();

        if (isDraft) {
            path = `${formStorage.data.definition.draftDef.draftEntitySet}(${getCleanDraftKey(formStorage.data.bindingContext.getKey())})/${DocumentEntity.Attachments}`;
        }

        const queryable = formStorage.oData.fromPath(path);

        try {
            if (!formStorage.data.bindingContext.isNew()) {
                await queryable.delete(attachmentId);
            }

            this.props.formStorage.setValueToEntityAndOrigEntity(attachmentsBc, attachments.filter(attachment => attachment.Id !== attachmentId));

            const isLastRemoved = getFiles(this.props.formStorage).length === 0;

            this.props.onFilesRemoved?.(isLastRemoved);
            this.selectFile(null);
        } catch (e) {
            this.props.setAlert({
                ...getAlertFromError(e),
                isSmall: false
            });
        }
    };

    handleFileNameChange = (e: ISmartFieldChange) => {
        const originalName = this.props.formStorage.getValue(this.getFileNameBc());
        const extension = getFileNameExtension(originalName);

        if (extension) {
            e.value = `${e.value || ""}.${extension}`;
        }

        this.props.formStorage.handleTemporalChange(e);
        this.props.formStorage.refreshFields();
    };

    handleFileNameCancel = (args: ISmartFieldTempDataActionArgs) => {
        this.props.formStorage.handleCancel(args)
            .then(() => this.props.formStorage.refreshFields());
    };

    /** Can be triggered by pressing enter or clicking on confirm button.*/
    handleFileNameConfirm = async (args: ISmartFieldTempDataActionArgs) => {
        const fileNameBc = this.getFileNameBc();
        const fileName = this.props.formStorage.getValue(fileNameBc);
        let { prefix } = splitFileName(fileName);

        prefix = removeWhiteSpace(prefix);

        // if the WriteLine is confirmed when empty,
        // restore the original file name
        if (!prefix) {
            await this.props.formStorage.handleCancel(args);
            this.props.formStorage.refreshFields();
            return;
        }

        const fileEntitySet = fileNameBc.getParent().getEntitySet();
        const queryable = this.props.formStorage.oData.fromPath(fileEntitySet.getName());

        const attachments = getFiles(this.props.formStorage);
        const uniqueName = getUniqueFileName(fileName, attachments.filter(attachment => attachment[this.filesDef.property].Id !== this.selectedFile.Id).map(attachment => attachment[this.filesDef.property].Name));

        await this.props.formStorage.handleConfirm(args);

        if (uniqueName !== fileName) {
            this.props.formStorage.setValue(fileNameBc, uniqueName);
        }

        this.props.formStorage.refreshFields();

        queryable.update(this.selectedFile.Id, {
            Name: uniqueName
        });

    };

    handleGridModeChange = (gridMode: GridMode) => {
        this.setState({ gridMode });
    };

    renderFileNameChange = () => {
        const fileNameBc = this.getFileNameBc();
        const { singlePreview } = this.filesDef;

        // form bc has changed, but selectedFile is not yet correctly updated
        if (!fileNameBc) {
            return null;
        }

        const isSelectedFileFormAttachment = singlePreview || this.getFormAttachments()?.some(attachment => attachment.File?.Id === this.selectedFile.Id);

        return (
            <SmartField storage={this.props.formStorage}
                        onTemporalChange={this.handleFileNameChange}
                        bindingContext={fileNameBc}
                        fieldDef={getFileNameWriteLineDef(this.props.formStorage.t)}
                        onConfirm={this.handleFileNameConfirm}
                        onCancel={this.handleFileNameCancel}
                        fieldProps={{
                            isDisabled: isSelectedFileFormAttachment
                        }}
                        style={{
                            // negative margin to shift the field into the padding area of the parent
                            marginTop: "-27px",
                            // prevent from overlapping with SplitPage control buttons
                            marginRight: "130px",
                            position: "absolute",
                            top: "38px",
                            left: "38px",
                            fontSize: "14px"
                        }}
            />
        );
    };

    renderFileViewer = () => {
        const fileNameBc = this.getFileNameBc();

        // form bc has changed, but selectedFile is not yet correctly updated
        if (!fileNameBc) {
            return null;
        }

        return (
            this.getFileViewer()
        );
    };

    handleFileViewerOnCustomFileAction = (actionId: string) => {
        this.props.formStorage.emitter.emit(ModelEvent.CustomFileAction,
            {
                file: this.selectedFile,
                action: actionId
            }
        );
    };

    getFileViewer = () => {
        const form = this.getFormDef();
        const { singlePreview } = this.filesDef;
        const mime = fileNameToMime(this.selectedFile.Name);
        const src = `${FILES_API_URL}/${this.selectedFile.Id}`;
        const isReadOnly = this.isFormReadOnly() || singlePreview;

        const customFileActions = isReadOnly ? [] : form?.getCustomAttachmentFileActions?.(this.selectedFile, this.props.formStorage);

        const isSelectedFileFormAttachment = this.getFormAttachments()?.some(attachment => attachment.File?.Id === this.selectedFile.Id);
        const attachmentsBc = this.getAttachmentsBc();
        const isRemoveDisabled = (attachmentsBc && !!this.props.formStorage.getBackendDisabledFieldMetadata(attachmentsBc)?.cannotDelete)
            || isSelectedFileFormAttachment || singlePreview;

        const hiddenItems: FileToolbarItem[] = [FileToolbarItem.Chat];

        if (singlePreview) {
            hiddenItems.push(FileToolbarItem.Dashboard);
        }

        const props: IFileViewerProps = {
            src,
            isReadOnly,
            fileName: this.selectedFile.Name,
            onGoToList: this.handleReturnFromFile,
            isRemoveDisabled,
            onFileRemove: this.handleFileRemove,
            hiddenItems,
            customFileActions: customFileActions ?? null,
            onCustomFileAction: this.handleFileViewerOnCustomFileAction
        };

        return getFileViewer(mime, props);
    };

    handleDragEnter = (event: DragEvent) => {
        this.props.onDragEnter?.(event);
    };

    handleDragLeave = (event: DragEvent) => {
        this.props.onDragLeave?.(event);
    };

    handleCustomFileAction = (file: IFileMetadataEntity, actionId: string, attachId: number) => {
        this.props.formStorage.emitter.emit(ModelEvent.CustomFileAction, { file: file, action: actionId, attachId });
    };

    getCustomFileActionsItems = (file: IFileMetadataEntity, storage: FormStorage): ISelectItem[] => {
        if (this.isFormReadOnly()) {
            return [];
        }
        const form = this.getFormDef();

        return form?.getCustomAttachmentFileActions?.(file, storage)?.map((item): ISelectItem => {
            const { id, label, iconName, isDisabled, isBusy } = item;
            return {
                id,
                label,
                iconName,
                isDisabled: isDisabled || isBusy
            };
        });

    };

    renderFormAttachmentsFolderTitle = () => {
        return <FormAttachmentsFolderTitle>
            {this.filesDef?.readOnlyAttachmentFolderName ?? this.props.formStorage.t("Components:FileUploader.FormAttachments")}&nbsp;
            <span>{`(${this.props.formStorage?.data?.definition?.title})`}</span>
        </FormAttachmentsFolderTitle>;
    };

    render() {
        const form = this.getFormDef();
        const { collection, property, singlePreview, readOnlyAttachmentPaths } = form?.files ?? {};
        const isBusy = this.props.isBusy || !this.props.formStorage?.loaded;
        const isReadOnlyMode = this.state.gridMode === GridMode.FormAttachments;

        return (<>
            {this.props.alert}
            {/*file name writeline (same for attachments title) has to be outside of scroll bar*/}
            {/*because it's in the padding area of the parent*/}
            {this.selectedFile &&
                this.renderFileNameChange()
            }
            {isReadOnlyMode && this.renderFormAttachmentsFolderTitle()}
            <FileViewStyled passRef={this._fileViewRef}
                            hotspotContextId={HotspotViewIds.FileView}
                            nonScrollableContent={isBusy && <BusyIndicator/>}>
                {this.selectedFile &&
                    this.renderFileViewer()
                }
                {!singlePreview && (
                    <SmartFileUploader isReadOnly={this.isFormReadOnly()}
                                   isHidden={!!this.selectedFile || !this.props.isVisible}
                                   storage={this.props.formStorage}
                                   collection={collection}
                                   fileProperty={property}
                                       gridMode={this.state.gridMode}
                                       onGridModeChange={this.handleGridModeChange}
                                   readOnlyAttachmentPaths={readOnlyAttachmentPaths}
                                   readOnlyAttachmentFolderName={this.filesDef?.readOnlyAttachmentFolderName}
                                   customFileActions={this.getCustomFileActionsItems}
                                   onCustomFileAction={this.handleCustomFileAction}
                                   onFileClick={this.handleFileChange}
                                   onNewFiles={this.handleNewFiles}
                                   onFilesUploaded={this.handleFilesUploaded}
                                   onFilesRemoved={this.handleFilesRemoved}
                                   onDragEnter={this.handleDragEnter}
                                   onDragLeave={this.handleDragLeave}
                                   otherFileDropAreas={this.props.otherFileDropAreas}/>
                )}
            </FileViewStyled>
        </>);
    }
}

export default withAlert({ position: AlertPosition.CenteredBottom })(withConfirmationDialog(FileView));